import React from "react";
import { FormattedMessage } from "react-intl";
import { useMutation } from "@apollo/client";
import {
  REMOVE_INTERVIEW_QUESTION,
  UPDATE_INTERVIEW_QUESTION,
  UPDATE_INTERVIEW_QUESTIONS,
  UPLOAD_INTERVIEW_INTRO_VIDEO,
  UPLOAD_INTERVIEW_QUESTION_VIDEO,
  DUPLICATE_INTERVIEW,
  REMOVE_INTERVIEW,
} from "graphql/mutations";
import { useModalConfirmation, useToast } from "hooks";

export default function useVideoPresentation() {
  const { toast } = useToast();
  const { show } = useModalConfirmation();
  const [updateInterviewQuestions, { loading: updatingQuestions }] = useMutation(UPDATE_INTERVIEW_QUESTIONS);
  const [updateInterviewQuestionVideo, { loading: uploadingQuestionVideo }] = useMutation(
    UPLOAD_INTERVIEW_QUESTION_VIDEO
  );
  const [deleteInterviewQuestion, { loading: deletingQuestion }] = useMutation(REMOVE_INTERVIEW_QUESTION);
  const [updateInterviewIntroVideo, { loading: uploadingIntroVideo }] = useMutation(UPLOAD_INTERVIEW_INTRO_VIDEO);
  const [updateInterviewQuestion, { loading: updatingQuestion }] = useMutation(UPDATE_INTERVIEW_QUESTION);
  const [duplicateInterview, { loading: duplicatingInterview }] = useMutation(DUPLICATE_INTERVIEW);
  const [removeInterview, { loading: removingInterview }] = useMutation(REMOVE_INTERVIEW);

  const handleInterviewQuestionsUpdate = ({
    interviewId,
    questions,
    onCompleted = () => {},
    missionRoleId,
    refetchQueries = [],
  }) => {
    updateInterviewQuestions({
      variables: {
        input: {
          interviewUuid: interviewId,
          missionRoleId,
          questions: questions.map(({ id, title, description, descriptionJson, position, timeLimitSeconds }) => ({
            title,
            description,
            descriptionJson,
            id,
            position,
            timeLimitSeconds,
          })),
        },
      },
      refetchQueries,
      onCompleted: () => {
        toast.success(<FormattedMessage id="useVideoPresentation.UpdateSuccess" />);
        onCompleted();
      },
      onError: () => {
        toast.error(<FormattedMessage id="useVideoPresentation.UpdateError" />);
      },
    });
  };

  const handleInterviewQuestionVideoUpload = (uuid, questionId, video) =>
    updateInterviewQuestionVideo({
      variables: {
        input: {
          uuid,
          questionId,
          video,
        },
      },
      onCompleted: () => {
        toast.success(<FormattedMessage id="useVideoPresentation.VideoUploadSuccess" />);
      },
      onError: () => {
        toast.error(<FormattedMessage id="useVideoPresentation.VideoUploadError" />);
      },
    });

  const handleInterviewIntroVideoUpload = (uuid, video) =>
    updateInterviewIntroVideo({
      variables: {
        input: {
          uuid,
          video,
        },
      },
      onCompleted: () => {
        toast.success(<FormattedMessage id="useVideoPresentation.IntroVideoUploadSuccess" />);
      },
      onError: () => {
        toast.error(<FormattedMessage id="useVideoPresentation.IntroVideoUploadError" />);
      },
    });

  const handleDeleteInterviewQuestion = ({ questionId, refetchQueries = [] }) => {
    deleteInterviewQuestion({
      variables: {
        input: {
          id: questionId,
        },
      },
      refetchQueries,
      onCompleted: () => {
        toast.success(<FormattedMessage id="useVideoPresentation.DeleteSuccess" />);
      },
      onError: (error) => {
        const hasResponsesError = error?.graphQLErrors?.some(
          (graphQLError) => graphQLError?.extensions?.code === "QUESTION_HAS_RESPONSES"
        );

        toast.error(
          <FormattedMessage
            id={hasResponsesError ? "useVideoPresentation.DeleteHasResponsesError" : "useVideoPresentation.DeleteError"}
          />
        );
      },
    });
  };

  const handleUpdateInterviewQuestion = ({ questionId, params }) => {
    updateInterviewQuestion({
      variables: {
        input: {
          id: questionId,
          params,
        },
      },
      onCompleted: () => {
        toast.success(<FormattedMessage id="useVideoPresentation.UpdateSuccess" />);
      },
      onError: (error) => {
        const hasResponsesError = error?.graphQLErrors?.some(
          (graphQLError) => graphQLError?.extensions?.code === "QUESTION_HAS_RESPONSES"
        );

        toast.error(
          <FormattedMessage
            id={hasResponsesError ? "useVideoPresentation.UpdateHasResponsesError" : "useVideoPresentation.UpdateError"}
          />
        );
      },
    });
  };

  const handleDuplicateInterview = ({ interviewId, newName, published, refetchQueries = [] }) => {
    duplicateInterview({
      variables: {
        input: {
          interviewId,
          params: {
            newName,
            published,
          },
        },
      },
      refetchQueries,
      onCompleted: () => {
        toast.success(<FormattedMessage id="useVideoPresentation.DuplicateSuccess" />);
      },
      onError: () => {
        toast.error(<FormattedMessage id="useVideoPresentation.DuplicateError" />);
      },
    });
  };

  const handleRemoveInterview = async ({ id, refetchQueries = [], withWarning = false }) => {
    if (withWarning) {
      const confirmed = await show(
        <FormattedMessage id="useVideoPresentation.DeleteInterviewConfirmation" />,
        <FormattedMessage id="useVideoPresentation.DeleteInterviewConfirmationDescription" />,
        {
          showCancel: true,
          showSave: false,
          showSaveAsDestructable: true,
          primaryButtonMessageId: "useVideoPresentation.DeleteInterviewConfirmation.Confirm",
        }
      );

      if (!confirmed) return;
    }

    removeInterview({
      variables: {
        input: {
          id,
        },
      },
      refetchQueries,
      onCompleted: () => {
        toast.success(<FormattedMessage id="useVideoPresentation.DeleteSuccessInterview" />);
      },
      onError: (error) => {
        toast.error(error.message || <FormattedMessage id="useVideoPresentation.DeleteErrorInterview" />);
      },
    });
  };

  const getMediaConstraints = (cameraId, micId) => ({
    video: {
      deviceId: cameraId ? { exact: cameraId } : undefined,
      width: { max: 640 },
      height: { max: 480 },
      frameRate: { max: 24 },
    },
    audio: {
      deviceId: micId ? { exact: micId } : undefined,
    },
  });

  return {
    handleInterviewQuestionsUpdate,
    handleInterviewQuestionVideoUpload,
    handleInterviewIntroVideoUpload,
    handleDeleteInterviewQuestion,
    handleUpdateInterviewQuestion,
    updatingQuestion,
    deletingQuestion,
    uploadingQuestionVideo,
    uploadingIntroVideo,
    updatingQuestions,
    handleDuplicateInterview,
    duplicatingInterview,
    handleRemoveInterview,
    removingInterview,
    getMediaConstraints,
  };
}
